/**
 * Error taxonomy mirroring the backend's stable codes
 * (see backend/internal/errors). Every non-2xx API response decodes
 * into one of these.
 *
 * Do NOT add new codes here without also adding them server-side.
 */

export type ApiErrorCode =
  | 'invalid_credentials'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation_error'
  // 本地 CV 校验及兼容错误码；上游 422 使用 validation_error，保留实际错误说明。
  | 'cv_unsupported_type'
  | 'cv_unparsable'
  | 'cv_too_large'
  | 'upstream_error'
  | 'upstream_unavailable'
  | 'rate_limited'
  | 'internal_error'
  // Client-side synthetic codes (never returned by the server)
  | 'network_error'
  | 'parse_error'

export interface ApiErrorPayload {
  error: ApiErrorCode | string
  message?: string
  fields?: Record<string, string>
}

/**
 * Single error class for every API failure. The fetch wrapper produces
 * exactly this shape; UI layers should `instanceof` check + branch on
 * `code` rather than parsing strings.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode | string
  readonly status: number
  readonly fields?: Record<string, string>

  constructor(opts: {
    code: ApiErrorCode | string
    message: string
    status: number
    fields?: Record<string, string>
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.code = opts.code
    this.status = opts.status
    this.fields = opts.fields
  }

  /** True for codes that should never surface as a toast (handled globally). */
  get isUnauthorized(): boolean {
    return this.code === 'unauthorized' || this.status === 401
  }

  /** True for codes the user can retry by hand. */
  get isRetryable(): boolean {
    return (
      this.code === 'upstream_error' ||
      this.code === 'upstream_unavailable' ||
      this.code === 'network_error' ||
      this.status >= 500
    )
  }
}

/** Parse a backend error response into an ApiError. */
export async function parseApiError(res: Response): Promise<ApiError> {
  let payload: ApiErrorPayload | undefined
  try {
    payload = (await res.json()) as ApiErrorPayload
  } catch {
    // body isn't JSON; fall through with status-only error
  }
  return new ApiError({
    code: payload?.error ?? `http_${res.status}`,
    message: payload?.message ?? res.statusText ?? 'Request failed',
    status: res.status,
    fields: payload?.fields,
  })
}
